/**
 * English dictionary — the source of truth for copy keys.
 * `zh` must be a full translation (type-checked against this object).
 */

export const en = {
  // Navigation
  navTools: 'Tools',
  navApi: 'API',
  navPricing: 'Pricing',
  navStart: 'Start Converting',
  navMenu: 'Toggle navigation menu',

  // Common
  commonTryAgain: 'Try again',
  commonErrorDefault: 'Something went wrong.',
  commonDownload: 'Download',
  commonNewFile: 'New file',
  commonBackHome: 'Back to home',
  commonRemoveFile: 'Remove {name}',

  // Hero
  heroBadge: '100% in-browser — files never leave your device',
  heroTitle1: 'Your Files.',
  heroTitle2: 'Your Browser.',
  heroTitle3: 'Your Privacy.',
  heroSubtitle: 'Convert, compress and manage files without uploading them.',
  heroCtaStart: 'Start Converting',
  heroCtaExplore: 'Explore Tools',
  heroNote: 'No account required · No uploads · No data collection',

  // Home tools grid
  homeToolsTitle: 'Everything you need, in one place',
  homeToolsSubtitle: 'Four essential file tools. Zero uploads. Zero signup. Just pick one and go.',

  // Tool names & descriptions
  toolVideo: 'Video Converter',
  toolAudio: 'Audio Converter',
  toolImage: 'Image Compressor',
  toolZip: 'ZIP Tools',
  toolVideoDesc: 'Convert MP4, MOV, MKV and WEBM to MP4 or WEBM.',
  toolAudioDesc: 'Transcode MP3, WAV and AAC audio files.',
  toolImageDesc: 'Shrink JPG, PNG and WEBP with visible savings.',
  toolZipDesc: 'Create and extract ZIP archives instantly.',

  // Features
  featPrivacyTitle: 'Privacy First',
  featPrivacyDesc:
    'Files are processed entirely in your browser with WebAssembly. Nothing is ever uploaded, logged or tracked.',
  featFastTitle: 'Fast',
  featFastDesc:
    'Native WASM performance runs on your own hardware — no queue, no waiting for a server round trip.',
  featEdgeTitle: 'Edge Powered',
  featEdgeDesc:
    'Built on Cloudflare Workers, D1, KV and R2, served from the edge in 300+ cities worldwide.',

  // Workflow
  flowTitle: 'How it works',
  flowSubtitle:
    'Your file moves through a local pipeline — React to Web Worker to WASM to Blob. The server never sees it.',
  flowSelectTitle: 'Select',
  flowSelectDesc: "Pick a file — drag & drop or browse from your device.",
  flowProcessTitle: 'Process',
  flowProcessDesc: 'WebAssembly runs in a Web Worker. Your device does the work.',
  flowGenerateTitle: 'Generate',
  flowGenerateDesc: 'The result is produced locally as a Blob. No network involved.',
  flowDownloadTitle: 'Download',
  flowDownloadDesc: "Save instantly. Your data never left your browser.",

  // CTA band
  ctaTitle: 'Your files, handled locally. No signup, no uploads.',
  ctaSubtitle: 'Pick a tool and start converting in seconds.',

  // File uploader
  upDrop: 'Drop it here',
  upDrag: 'Drag & drop a file',
  upOr: 'or',
  upBrowse: 'browse',
  upFromDevice: 'from your device',
  upLimit: '{formats} — up to {maxSizeMB} MB',
  upLimitPerFile: 'up to {maxSizeMB} MB per file',
  upInvalidType: 'Unsupported file type "{ext}". Supported: {accept}',
  upTooLarge: '"{name}" is too large. Maximum size is {maxSize}.',

  // File info
  fiSize: '{size}',

  // Progress
  progressDefault: 'Processing…',

  // Result card
  resultDone: 'Done — file ready to download',

  // ZIP compress
  compCreating: 'Creating archive…',
  compProgress: 'Compressing',
  compSelectedOne: '{n} file selected',
  compSelectedMany: '{n} files selected',
  compClear: 'Clear all',
  compAction: 'Compress to ZIP',
  compFailed: 'Compression failed. Please try again with different files.',

  // ZIP extract
  extExtracting: 'Extracting archive…',
  extProgress: 'Extracting',
  extExtractedOne: '{n} file extracted',
  extExtractedMany: '{n} files extracted',
  extSave: 'Save',
  extDownloadAll: 'Download all',
  extNewArchive: 'New archive',
  extAction: 'Extract',
  extFailed: 'Could not extract this archive. The file may be corrupted or password protected.',

  // Image
  imgOutputFormat: 'Output format',
  imgSelectFormat: 'Select a format',
  imgFormatWebp: 'WebP (recommended)',
  imgQuality: 'Quality',
  imgAction: 'Compress Image',
  imgFailed: 'Compression failed. Please try another image.',
  imgProcessing: 'Compressing image…',
  imgProgress: 'Compressing',
  imgResultComplete: 'Compression complete',
  imgPreviewAlt: 'Compressed preview',
  imgLoadingPreview: 'Loading preview…',
  imgOriginal: 'Original',
  imgCompressed: 'Compressed',
  imgSaved: 'Saved',
  imgLarger: '{pct}% larger',
  imgNewImage: 'New image',

  // Media convert (video/audio)
  mediaLoading: 'Loading conversion engine…',
  mediaConverting: 'Converting…',
  mediaEngineNote: 'First run downloads the WASM engine (cached after that)',
  mediaBrowserNote: 'This runs entirely in your browser',
  mediaOutputFormat: 'Output format',
  mediaSelectFormat: 'Select a format',
  mediaAction: 'Convert',
  mediaLocalNote: 'Files are processed locally via WebAssembly — nothing is uploaded.',
  convertFailed: 'Conversion failed. Please try again with a different file.',

  // Page SEO
  seoHomeTitle: 'CloudTools — Your Files. Your Browser. Your Privacy.',
  seoHomeDesc:
    'Convert, compress and manage files without uploading them. Privacy-first online tools powered by WebAssembly and Cloudflare Edge.',
  seoHomeKw: 'online converter, video converter, image compressor, zip tool, privacy first, browser wasm',
  seoVideoTitle: 'MP4 Converter Online — Free Video Converter',
  seoVideoDesc:
    'Convert MP4, MOV, MKV, WEBM videos to MP4 or WEBM entirely in your browser. No uploads, files never leave your device.',
  seoVideoKw: 'mp4 converter, video converter, webm converter, online video converter',
  seoAudioTitle: 'Audio Converter Online — MP3 WAV AAC',
  seoAudioDesc:
    'Convert MP3, WAV and AAC audio files in your browser with WebAssembly. Private, fast and free.',
  seoAudioKw: 'audio converter, mp3 converter, wav converter, online audio converter',
  seoImageTitle: 'Image Compressor Online — Compress JPG PNG WEBP',
  seoImageDesc:
    'Compress JPG, PNG and WEBP images in your browser. See size before, after and savings ratio. No uploads, full privacy.',
  seoImageKw: 'image compressor, jpg compressor, png compressor, webp compressor',
  seoZipTitle: 'ZIP Tool Online — Compress and Extract ZIP',
  seoZipDesc: 'Create and extract ZIP archives in your browser. Files never leave your device.',
  seoZipKw: 'zip tool, zip compressor, unzip online, zip extractor',
  seo404Title: 'Page Not Found — CloudTools',
  seo404Desc: 'The page you are looking for does not exist.',

  // Tool page headers
  toolPageVideo: 'Video Converter',
  toolPageVideoDesc:
    'Convert MP4, MOV, MKV and WEBM videos to MP4 or WEBM — powered by FFmpeg.wasm in your browser.',
  toolPageAudio: 'Audio Converter',
  toolPageAudioDesc:
    'Transcode MP3, WAV and AAC audio locally with FFmpeg.wasm — your audio never leaves your browser.',
  toolPageImage: 'Image Compressor',
  toolPageImageDesc:
    'Shrink JPG, PNG and WEBP images locally with adjustable quality — your image never leaves your browser.',
  toolPageZip: 'ZIP Tools',
  toolPageZipDesc: 'Compress files into an archive or extract existing ones — entirely in your browser.',

  // ZIP mode switch
  modeCompress: 'Compress',
  modeExtract: 'Extract',

  // 404
  nfBody: 'This page does not exist.',

  // Footer
  footTagline:
    'Convert, compress and manage files entirely in your browser. Powered by WebAssembly — your files never leave your device.',
  footTools: 'Tools',
  footCompany: 'Company',
  footPrivacy: 'Privacy',
  footApi: 'API',
  footPricing: 'Pricing',
  footRights: '© {year} CloudTools. All processing happens locally in your browser.',
  footApiVersion: 'API v{version}',
} as const

export type DictKey = keyof typeof en